import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc,
  updateDoc,
  deleteDoc,
  query, 
  where, 
  orderBy,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  getCountFromServer,
  getAggregateFromServer,
  sum,
  count,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';
import { Product, Review, MonthlySnapshot } from '../types';
import { deleteImagesByUrls } from './imageService';

export interface InternationalShippingImportUpdateItem {
  id: string;
  product_description?: string;
  manufacturer?: string;
  origin_country?: string;
  hs_code?: string;
  confidence?: string;
}

export interface InternationalShippingImportResult {
  attempted: number;
  updated: number;
  failed: number;
  failures: Array<{ id: string; error: string }>;
}

export interface NormalizePricesResult {
  processed: number;
  updated: number;
  errors: number;
}

export interface CommodityCodesImportUpdateItem {
  id: string;
  commodityCodes: Record<string, string>;
}

export interface CommodityCodesImportResult {
  attempted: number;
  updated: number;
  failed: number;
  failures: Array<{ id: string; error: string }>;
}

const normalizeOptionalString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const normalizeCommodityCodes = (value: unknown): Record<string, string> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;

  const codes: Record<string, string> = {};
  for (const [key, code] of Object.entries(value as Record<string, unknown>)) {
    if (typeof key !== 'string') continue;
    if (typeof code !== 'string') continue;
    const trimmed = code.trim();
    if (!trimmed) continue;
    codes[key.toUpperCase()] = trimmed;
  }

  return Object.keys(codes).length > 0 ? codes : null;
};

/**
 * Bulk apply international shipping/customs updates from a JSON-like list.
 * Updates: description, manufacturer, originCountry, hsCode (+ optional confidence field).
 */
export const applyInternationalShippingImportUpdates = async (
  items: InternationalShippingImportUpdateItem[]
): Promise<InternationalShippingImportResult> => {
  const failures: Array<{ id: string; error: string }> = [];
  let updated = 0;

  const attemptedItems = Array.isArray(items) ? items : [];

  for (const rawItem of attemptedItems) {
    const id = normalizeOptionalString((rawItem as any)?.id);
    if (!id) continue;

    const description = normalizeOptionalString((rawItem as any)?.product_description);
    const manufacturer = normalizeOptionalString((rawItem as any)?.manufacturer);
    const originCountry = normalizeOptionalString((rawItem as any)?.origin_country)?.toUpperCase();
    const hsCode = normalizeOptionalString((rawItem as any)?.hs_code);
    const confidence = normalizeOptionalString((rawItem as any)?.confidence);

    const updatePayload: Record<string, any> = { updatedAt: serverTimestamp() };
    if (description !== undefined) updatePayload.description = description;
    if (manufacturer !== undefined) updatePayload.manufacturer = manufacturer;
    if (originCountry !== undefined) updatePayload.originCountry = originCountry;
    if (hsCode !== undefined) updatePayload.hsCode = hsCode;
    if (confidence !== undefined) updatePayload.internationalShippingConfidence = confidence;

    // If nothing besides updatedAt, skip
    if (Object.keys(updatePayload).length === 1) continue;

    try {
      await updateDoc(doc(db, 'products', id), updatePayload);
      updated++;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      failures.push({ id, error: message });
    }
  }

  return {
    attempted: attemptedItems.length,
    updated,
    failed: failures.length,
    failures,
  };
};

/**
 * Bulk set `commodityCodes` from a JSON-like list.
 * Each item overwrites the product's `commodityCodes` object.
 */
export const applyCommodityCodesImportUpdates = async (
  items: CommodityCodesImportUpdateItem[]
): Promise<CommodityCodesImportResult> => {
  const failures: Array<{ id: string; error: string }> = [];
  let updated = 0;

  const attemptedItems = Array.isArray(items) ? items : [];

  for (const rawItem of attemptedItems) {
    const id = normalizeOptionalString((rawItem as any)?.id);
    if (!id) continue;

    const commodityCodes = normalizeCommodityCodes((rawItem as any)?.commodityCodes);
    if (!commodityCodes) continue;

    try {
      await updateDoc(doc(db, 'products', id), {
        commodityCodes,
        updatedAt: serverTimestamp(),
      });
      updated++;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      failures.push({ id, error: message });
    }
  }

  return {
    attempted: attemptedItems.length,
    updated,
    failed: failures.length,
    failures,
  };
};



/**
 * Calculate inventory value for a product
 */
const calculateInventoryValue = (price: number = 0, stockQuantity: number = 0): number => {
  return price * stockQuantity;
};

// Pagination result type
export interface PaginatedProductsResult {
  products: Product[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
  total?: number;
}

// Search result type
export interface SearchProductsResult {
  products: Product[];
  total: number;
}

/**
 * Helper function to process product data consistently
 */
const processProductData = (doc: QueryDocumentSnapshot<DocumentData>): Product => {
  const data = doc.data();
  

  
  // Process reviews to ensure they have the correct fields
  const reviews = data.reviews || [];
  
  // Calculate inventory value if missing (for backward compatibility)
  const price = data.price || 0;
  const stockQuantity = data.stockQuantity || 0;
  const inventoryValue = data.inventoryValue !== undefined 
    ? data.inventoryValue 
    : calculateInventoryValue(price, stockQuantity);
  
  // Helper function to convert Firestore timestamp to Date
  const convertTimestamp = (timestamp: any): Date => {
    if (!timestamp) {
      return new Date();
    }
    
    // Handle Firestore Timestamp objects
    if (timestamp && typeof timestamp === 'object' && timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    
    // Handle regular Date objects
    if (timestamp instanceof Date) {
      return timestamp;
    }
    
    // Handle string dates
    if (typeof timestamp === 'string') {
      const date = new Date(timestamp);
      return isNaN(date.getTime()) ? new Date() : date;
    }
    
    // Handle number timestamps
    if (typeof timestamp === 'number') {
      return new Date(timestamp);
    }
    
    // Fallback to current date
    return new Date();
  };
  
  return {
    id: doc.id,
    name: data.name || '',
    description: data.description || '',
    price: price,
    jamaicanPrice: typeof data.jamaicanPrice === 'number' ? data.jamaicanPrice : undefined,
    onSalePrice: data.onSalePrice || null,
    image: data.image || (data.images && data.images[0]) || '/api/placeholder/300/300',
    images: data.images || ['/api/placeholder/300/300'],
    category: data.category || 'Uncategorized',
    brand: data.brand || null,
    tags: data.tags || [],
    isNew: data.isNew || false,
    isBestSeller: data.isBestSeller || false,
    onSale: data.onSale || false,
    disabled: data.disabled,
    stockQuantity: stockQuantity,
    lowStockThreshold: data.lowStockThreshold || 10,
    netWeight: data.netWeight !== undefined ? data.netWeight : null,
    weightUnit: data.weightUnit || 'g',
    listingQuantity: data.listingQuantity !== undefined ? data.listingQuantity : 1,
    inventoryValue: inventoryValue,
    availableShippingOptions: data.availableShippingOptions || [], // Default to empty array, will use all options if empty
    seoTitle: data.seoTitle || null,
    seoDescription: data.seoDescription || null,
    salesCount: data.salesCount || 0,
    salesThisMonth: data.salesThisMonth || 0,
    revenueThisMonth: data.revenueThisMonth || 0,
    lastStatsUpdate: data.lastStatsUpdate ? convertTimestamp(data.lastStatsUpdate) : undefined,
    variations: data.variations || [],
    parentVariationGroup: data.parentVariationGroup || null,
    reviews: reviews,
    // EasyPost customs fields
    manufacturer: data.manufacturer || '',
    hsCode: data.hsCode || '',
    originCountry: data.originCountry || '',
    commodityCodes: data.commodityCodes || undefined,
    createdAt: convertTimestamp(data.createdAt),
    updatedAt: convertTimestamp(data.updatedAt)
  } as Product;
};

/**
 * Get the total count of products
 */
export const getProductCount = async (searchQuery?: string, category?: string): Promise<number> => {
  try {
    console.log('getProductCount called with:', { searchQuery, category });
    
    const productsCollection = collection(db, 'products');
    let countQuery = query(productsCollection);
    
    // Add category filter if specified
    if (category && category !== 'All') {
      countQuery = query(productsCollection, where('category', '==', category));
    }
    
    const snapshot = await getCountFromServer(countQuery);
    const count = snapshot.data().count;
    console.log('Product count result:', count);
    
    return count;
  } catch (error) {
    console.error('Error getting product count:', error);
    throw new Error('Failed to get product count');
  }
};

/**
 * Fetch products with pagination support (simplified version for debugging)
 */
export const fetchProductsPaginatedSimple = async (
  pageSize: number = 20
): Promise<PaginatedProductsResult> => {
  try {
    console.log('fetchProductsPaginatedSimple called with pageSize:', pageSize);
    
    const productsCollection = collection(db, 'products');
    
    // Simple query without complex ordering
    const productQuery = query(productsCollection, limit(pageSize));
    
    console.log('Executing simple Firestore query...');
    const snapshot = await getDocs(productQuery);
    console.log('Simple query result:', { docsCount: snapshot.docs.length, empty: snapshot.empty });
    
    if (!snapshot.empty) {
      console.log('First document data:', snapshot.docs[0].data());
    }
    
    const products = snapshot.docs.map(processProductData);
    console.log('Processed products:', products.length);
    
    return {
      products: products,
      lastDoc: null,
      hasMore: false,
    };
  } catch (error) {
    console.error('Error in simple fetch:', error);
    throw new Error('Failed to fetch products (simple)');
  }
};

/**
 * Fetch products with pagination support
 */
export const fetchProductsPaginated = async (
  pageSize: number = 20,
  lastDoc: QueryDocumentSnapshot<DocumentData> | null = null,
  searchQuery?: string,
  category?: string
): Promise<PaginatedProductsResult> => {
  try {
    console.log('fetchProductsPaginated called with:', { pageSize, lastDoc: !!lastDoc, searchQuery, category });
    
    const productsCollection = collection(db, 'products');
    
    // Build base query - use __name__ (document ID) for consistent ordering
    let productQuery = query(
      productsCollection,
      orderBy('__name__')
    );
    
    // Add category filter if specified
    if (category && category !== 'All' && category !== 'Uncategorized') {
      productQuery = query(
        productsCollection,
        where('category', '==', category),
        orderBy('__name__')
      );
    }
    
    // Add pagination
    if (lastDoc) {
      productQuery = query(productQuery, startAfter(lastDoc));
    }
    
    productQuery = query(productQuery, limit(pageSize));
    
    console.log('Executing Firestore query...');
    const snapshot = await getDocs(productQuery);
    console.log('Firestore query result:', { docsCount: snapshot.docs.length, empty: snapshot.empty });
    
    let products = snapshot.docs.map(processProductData);
    console.log('Processed products:', products.length);
    
    // Apply client-side category filtering for "Uncategorized"
    if (category === 'Uncategorized') {
      products = products.filter(product => 
        !product.category || product.category === '' || product.category === 'Uncategorized'
      );
      console.log('Filtered uncategorized products:', products.length);
    }
    
    // Don't filter by search query here - that should be handled by the search function
    const hasMore = snapshot.docs.length === pageSize;
    const lastDocument = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
    
    return {
      products: products,
      lastDoc: lastDocument,
      hasMore,
    };
  } catch (error) {
    console.error('Error fetching products:', error);
    throw new Error('Failed to fetch products');
  }
};

/**
 * Search products by name or description
 */
export const searchProducts = async (
  searchQuery: string,
  category?: string,
  brand?: string
): Promise<SearchProductsResult> => {
  try {
    const productsCollection = collection(db, 'products');
    let productQuery = query(productsCollection, orderBy('name'));
    
    // Build filters array
    const filters = [];
    
    // Add category filter if specified
    if (category && category !== 'All') {
      if (category === 'Uncategorized') {
        // For uncategorized, we'll handle this in client-side filtering
        // since Firestore can't easily query for null/empty fields
      } else {
        filters.push(where('category', '==', category));
      }
    }
    
    // Add brand filter if specified
    if (brand && brand !== 'All') {
      filters.push(where('brand', '==', brand));
    }
    
    // Apply filters if any exist
    if (filters.length > 0) {
      productQuery = query(
        productsCollection,
        ...filters,
        orderBy('name')
      );
    }
    
    const snapshot = await getDocs(productQuery);
    let allProducts = snapshot.docs.map(processProductData);
    
    // Apply client-side category filtering for "Uncategorized"
    if (category === 'Uncategorized') {
      allProducts = allProducts.filter(product => 
        !product.category || product.category === '' || product.category === 'Uncategorized'
      );
    }
    
    // Filter by search query
    const searchLower = searchQuery.toLowerCase();
    const filteredProducts = allProducts.filter(product => 
      product.name.toLowerCase().includes(searchLower) ||
      product.description.toLowerCase().includes(searchLower) ||
      (product.category && product.category.toLowerCase().includes(searchLower)) ||
      (product.brand && product.brand.toLowerCase().includes(searchLower))
    );
    
    return {
      products: filteredProducts,
      total: filteredProducts.length
    };
  } catch (error) {
    console.error('Error searching products:', error);
    throw new Error('Failed to search products');
  }
};

/**
 * Fetch a single product by ID
 */
export const fetchProductById = async (productId: string): Promise<Product | null> => {
  try {
    const productDoc = doc(db, 'products', productId);
    const snapshot = await getDoc(productDoc);
    
    if (!snapshot.exists()) {
      return null;
    }
    
    return processProductData(snapshot);
  } catch (error) {
    console.error('Error fetching product:', error);
    throw new Error('Failed to fetch product');
  }
};

/**
 * Get all unique categories
 */
export const getProductCategories = async (): Promise<string[]> => {
  try {
    const productsCollection = collection(db, 'products');
    const snapshot = await getDocs(query(productsCollection));
    
    const categories = new Set<string>();
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.category) {
        categories.add(data.category);
      }
    });
    
    return Array.from(categories).sort();
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw new Error('Failed to fetch categories');
  }
};

/**
 * Fetch all products (for sale events and other bulk operations)
 */
export const fetchAllProducts = async (): Promise<Product[]> => {
  try {
    const productsCollection = collection(db, 'products');
    const snapshot = await getDocs(query(productsCollection, orderBy('name')));
    
    return snapshot.docs.map(processProductData);
  } catch (error) {
    console.error('Error fetching all products:', error);
    throw new Error('Failed to fetch all products');
  }
};

/**
 * Get product statistics using efficient aggregation queries
 */
export interface ProductStatistics {
  totalProducts: number;
  lowStockProducts: number;
  outOfStockProducts: number;
  totalInventoryValue: number;
}

/**
 * Get total product count using aggregation (active products only)
 */
export const getTotalProductCount = async (): Promise<number> => {
  try {
    console.log('Getting total active product count with aggregation...');
    
    const productsCollection = collection(db, 'products');
    const snapshot = await getAggregateFromServer(
      query(
        productsCollection,
        where('disabled', '!=', true)
      ),
      { totalCount: count() }
    );
    
    const total = snapshot.data().totalCount;
    console.log('Total active products (aggregation):', total);
    
    return total;
  } catch (error) {
    console.error('Error getting total product count:', error);
    throw new Error('Failed to get total product count');
  }
};

/**
 * Get low stock product count using aggregation (active products only)
 */
export const getLowStockProductCount = async (threshold: number = 10): Promise<number> => {
  try {
    console.log('Getting low stock active product count with aggregation...');
    
    const productsCollection = collection(db, 'products');
    const snapshot = await getAggregateFromServer(
      query(
        productsCollection,
        where('disabled', '!=', true),
        where('stockQuantity', '>', 0),
        where('stockQuantity', '<=', threshold)
      ),
      { lowStockCount: count() }
    );
    
    const lowStock = snapshot.data().lowStockCount;
    console.log('Low stock active products (aggregation):', lowStock);
    
    return lowStock;
  } catch (error) {
    console.error('Error getting low stock product count:', error);
    throw new Error('Failed to get low stock product count');
  }
};

/**
 * Get out of stock product count using aggregation (active products only)
 * Includes active products with stockQuantity = 0 AND active products without stockQuantity field
 */
export const getOutOfStockProductCount = async (): Promise<number> => {
  try {
    console.log('Getting out of stock active product count with aggregation...');
    
    const productsCollection = collection(db, 'products');
    
    // Get total active products and active products with stock > 0, then subtract
    // This efficiently includes active products with stockQuantity = 0 OR missing stockQuantity field
    const [totalSnapshot, inStockSnapshot] = await Promise.all([
      getAggregateFromServer(
        query(
          productsCollection,
          where('disabled', '!=', true)
        ),
        { totalCount: count() }
      ),
      getAggregateFromServer(
        query(
          productsCollection,
          where('disabled', '!=', true),
          where('stockQuantity', '>', 0)
        ),
        { inStockCount: count() }
      )
    ]);
    
    const totalProducts = totalSnapshot.data().totalCount;
    const inStockProducts = inStockSnapshot.data().inStockCount;
    const outOfStock = totalProducts - inStockProducts;
    
    console.log('Out of stock calculation (active only):', { 
      total: totalProducts, 
      inStock: inStockProducts, 
      outOfStock 
    });
    
    return outOfStock;
  } catch (error) {
    console.error('Error getting out of stock product count:', error);
    throw new Error('Failed to get out of stock product count');
  }
};

/**
 * Get total inventory value using aggregation (active products only)
 * Now uses pre-computed inventoryValue field for fast aggregation of active products
 */
export const getTotalInventoryValue = async (): Promise<number> => {
  try {
    console.log('Getting total inventory value for active products with aggregation...');
    
    const productsCollection = collection(db, 'products');
    const snapshot = await getAggregateFromServer(
      query(
        productsCollection,
        where('disabled', '!=', true)
      ),
      { totalInventoryValue: sum('inventoryValue') }
    );
    
    const totalValue = snapshot.data().totalInventoryValue || 0;
    console.log('Total inventory value for active products (aggregation):', totalValue);
    
    return totalValue;
  } catch (error) {
    console.error('Error getting total inventory value:', error);
    throw new Error('Failed to get total inventory value');
  }
};

/**
 * Get all product statistics efficiently
 */
export const getProductStatistics = async (): Promise<ProductStatistics> => {
  try {
    console.log('Getting all product statistics...');
    
    // Run all aggregation queries in parallel for better performance
    const [
      totalProducts,
      lowStockProducts,
      outOfStockProducts,
      totalInventoryValue
    ] = await Promise.all([
      getTotalProductCount(),
      getLowStockProductCount(),
      getOutOfStockProductCount(),
      getTotalInventoryValue()
    ]);
    
    const stats = {
      totalProducts,
      lowStockProducts,
      outOfStockProducts,
      totalInventoryValue
    };
    
    console.log('Product statistics:', stats);
    return stats;
  } catch (error) {
    console.error('Error getting product statistics:', error);
    throw new Error('Failed to get product statistics');
  }
};

/**
 * Create a new product
 */
export const createProduct = async (productData: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    console.log('Creating new product:', productData);
    
    const productsCollection = collection(db, 'products');
    
    // Calculate inventory value
    const price = productData.price || 0;
    const stockQuantity = productData.stockQuantity || 0;
    const inventoryValue = calculateInventoryValue(price, stockQuantity);
    
    // Prepare product data with timestamps
    const newProduct = {
      ...productData,
      inventoryValue, // Add pre-computed inventory value
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // Ensure required fields have defaults
      salesCount: productData.salesCount || 0,
      stockQuantity: stockQuantity,
      lowStockThreshold: productData.lowStockThreshold || 10,
      isNew: productData.isNew || false,
      isBestSeller: productData.isBestSeller || false,
      onSale: productData.onSale || false,
      reviews: productData.reviews || [],
      tags: productData.tags || [],
    };
    
    const docRef = await addDoc(productsCollection, newProduct);
    console.log('Product created with ID:', docRef.id, 'Inventory Value:', inventoryValue);
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating product:', error);
    throw new Error('Failed to create product');
  }
};

/**
 * Update an existing product
 */
export const updateProduct = async (productId: string, productData: Partial<Product>): Promise<void> => {
  try {
    console.log('Updating product:', productId, productData);
    
    const productDoc = doc(db, 'products', productId);
    
    // Prepare update data with timestamp
    let updateData = {
      ...productData,
      updatedAt: serverTimestamp(),
    };
    
    // Recalculate inventory value if price or stockQuantity is being updated
    if (productData.price !== undefined || productData.stockQuantity !== undefined) {
      // Get current product data if we need missing values
      if (productData.price !== undefined && productData.stockQuantity === undefined) {
        const currentDoc = await getDoc(productDoc);
        if (currentDoc.exists()) {
          const currentData = currentDoc.data();
          const stockQuantity = currentData.stockQuantity || 0;
          updateData.inventoryValue = calculateInventoryValue(productData.price, stockQuantity);
        }
      } else if (productData.stockQuantity !== undefined && productData.price === undefined) {
        const currentDoc = await getDoc(productDoc);
        if (currentDoc.exists()) {
          const currentData = currentDoc.data();
          const price = currentData.price || 0;
          updateData.inventoryValue = calculateInventoryValue(price, productData.stockQuantity);
        }
      } else {
        // Both price and stockQuantity are provided
        updateData.inventoryValue = calculateInventoryValue(
          productData.price || 0, 
          productData.stockQuantity || 0
        );
      }
    }
    
    // Remove id, createdAt from update data if present
    delete updateData.id;
    delete updateData.createdAt;
    
    await updateDoc(productDoc, updateData);
    console.log('Product updated successfully');
  } catch (error) {
    console.error('Error updating product:', error);
    throw new Error('Failed to update product');
  }
};

/**
 * Toggle product disabled status (soft delete/restore)
 */
export const toggleProductDisabled = async (productId: string, disabled: boolean): Promise<void> => {
  try {
    console.log('Toggling product disabled status:', productId, disabled);
    
    await updateProduct(productId, { disabled });
    
    console.log('Product disabled status updated successfully');
  } catch (error) {
    console.error('Error toggling product disabled status:', error);
    throw new Error('Failed to update product status');
  }
};

/**
 * Bulk enable all products in the database
 */
export const enableAllProducts = async (): Promise<{ updated: number; errors: number }> => {
  try {
    console.log('Starting bulk enable all products operation');
    
    const productsCollection = collection(db, 'products');
    const disabledQuery = query(productsCollection, where('disabled', '==', true));
    const snapshot = await getDocs(disabledQuery);
    
    if (snapshot.empty) {
      console.log('No disabled products found');
      return { updated: 0, errors: 0 };
    }
    
    const batch = writeBatch(db);
    let updateCount = 0;
    
    snapshot.docs.forEach((doc) => {
      batch.update(doc.ref, { 
        disabled: false,
        updatedAt: serverTimestamp()
      });
      updateCount++;
    });
    
    await batch.commit();
    
    console.log(`Successfully enabled ${updateCount} products`);
    return { updated: updateCount, errors: 0 };
  } catch (error) {
    console.error('Error enabling all products:', error);
    throw new Error('Failed to enable all products');
  }
};

/**
 * Bulk disable all products in the database
 */
export const disableAllProducts = async (): Promise<{ updated: number; errors: number }> => {
  try {
    console.log('Starting bulk disable all products operation');
    
    const productsCollection = collection(db, 'products');
    const enabledQuery = query(productsCollection, where('disabled', '!=', true));
    const snapshot = await getDocs(enabledQuery);
    
    if (snapshot.empty) {
      console.log('No enabled products found');
      return { updated: 0, errors: 0 };
    }
    
    const batch = writeBatch(db);
    let updateCount = 0;
    
    snapshot.docs.forEach((doc) => {
      batch.update(doc.ref, { 
        disabled: true,
        updatedAt: serverTimestamp()
      });
      updateCount++;
    });
    
    await batch.commit();
    
    console.log(`Successfully disabled ${updateCount} products`);
    return { updated: updateCount, errors: 0 };
  } catch (error) {
    console.error('Error disabling all products:', error);
    throw new Error('Failed to disable all products');
  }
};

/**
 * Bulk remove new flag from all products that have isNew set to true
 */
export const removeNewFlagFromAllProducts = async (): Promise<{ updated: number; errors: number }> => {
  try {
    console.log('Starting bulk remove new flag from all products operation');
    
    const productsCollection = collection(db, 'products');
    const newProductsQuery = query(productsCollection, where('isNew', '==', true));
    const snapshot = await getDocs(newProductsQuery);
    
    if (snapshot.empty) {
      console.log('No products with "new" flag found');
      return { updated: 0, errors: 0 };
    }
    
    const batch = writeBatch(db);
    let updateCount = 0;
    
    snapshot.docs.forEach((doc) => {
      batch.update(doc.ref, { 
        isNew: false,
        updatedAt: serverTimestamp()
      });
      updateCount++;
    });
    
    await batch.commit();
    
    console.log(`Successfully removed "new" flag from ${updateCount} products`);
    return { updated: updateCount, errors: 0 };
  } catch (error) {
    console.error('Error removing new flags from products:', error);
    throw new Error('Failed to remove new flags from products');
  }
};

/**
 * Round all enabled products' regular and sale prices to 2 decimal places
 */
export const normalizeEnabledProductPrices = async (): Promise<NormalizePricesResult> => {
  try {
    const productsCollection = collection(db, 'products');
    const enabledQuery = query(productsCollection, where('disabled', '!=', true));
    const snapshot = await getDocs(enabledQuery);

    if (snapshot.empty) {
      return { processed: 0, updated: 0, errors: 0 };
    }

    let processed = 0;
    let updated = 0;
    let errors = 0;

    for (const docSnap of snapshot.docs) {
      processed++;
      const data = docSnap.data() as any;

      const currentPrice = typeof data.price === 'number' ? data.price : null;
      const currentSale = typeof data.onSalePrice === 'number' ? data.onSalePrice : null;

      if (currentPrice === null && currentSale === null) {
        continue;
      }

      const roundedPrice = currentPrice !== null ? Number(currentPrice.toFixed(2)) : null;
      const roundedSale = currentSale !== null ? Number(currentSale.toFixed(2)) : null;

      const needsUpdate =
        (currentPrice !== null && roundedPrice !== currentPrice) ||
        (currentSale !== null && roundedSale !== currentSale);

      if (!needsUpdate) continue;

      try {
        await updateDoc(docSnap.ref, {
          price: roundedPrice,
          onSalePrice: roundedSale,
          updatedAt: serverTimestamp(),
        });
        updated++;
      } catch (err) {
        console.error('Failed to normalize prices for product', docSnap.id, err);
        errors++;
      }
    }

    return { processed, updated, errors };
  } catch (error) {
    console.error('Error normalizing enabled product prices:', error);
    throw new Error('Failed to normalize enabled product prices');
  }
};

/**
 * Delete a product
 */
export const deleteProduct = async (productId: string): Promise<void> => {
  try {
    console.log('Deleting product:', productId);
    
    // First, fetch the product to get its images
    const productDoc = doc(db, 'products', productId);
    const productSnapshot = await getDoc(productDoc);
    
    if (productSnapshot.exists()) {
      const productData = productSnapshot.data() as Product;
      const images = productData.images || [];
      
      // Delete Firebase Storage images (filter out external URLs)
      const firebaseStorageImages = images.filter(imageUrl => 
        imageUrl.includes('firebasestorage.googleapis.com')
      );
      
      if (firebaseStorageImages.length > 0) {
        console.log(`Deleting ${firebaseStorageImages.length} images from Firebase Storage`);
        await deleteImagesByUrls(firebaseStorageImages);
      }
    }
    
    // Delete the product document
    await deleteDoc(productDoc);
    
    console.log('Product and associated images deleted successfully');
  } catch (error) {
    console.error('Error deleting product:', error);
    throw new Error('Failed to delete product');
  }
};

/**
 * Validate product data
 */
export const validateProductData = (productData: Partial<Product>): string[] => {
  const errors: string[] = [];
  
  if (!productData.name || productData.name.trim().length === 0) {
    errors.push('Product name is required');
  }
  
  if (!productData.description || productData.description.trim().length === 0) {
    errors.push('Product description is required');
  }
  
  if (productData.price === undefined || productData.price === null || productData.price < 0) {
    errors.push('Product price must be 0 or greater');
  }

  if (productData.jamaicanPrice !== undefined && productData.jamaicanPrice !== null && productData.jamaicanPrice < 0) {
    errors.push('Jamaican price must be 0 or greater');
  }
  
  if (productData.onSalePrice && productData.price !== undefined && productData.price > 0 && productData.onSalePrice >= productData.price) {
    errors.push('Sale price must be less than regular price');
  }
  
  if (productData.onSalePrice && productData.price === 0) {
    errors.push('Sale price cannot be set for free products (price = $0)');
  }
  
  if (!productData.image || productData.image.trim().length === 0) {
    errors.push('Product image is required');
  }
  
  if (!productData.images || productData.images.length === 0) {
    errors.push('At least one product image is required');
  }
  
  if (productData.stockQuantity !== undefined && productData.stockQuantity < 0) {
    errors.push('Stock quantity cannot be negative');
  }
  
  if (productData.lowStockThreshold !== undefined && productData.lowStockThreshold < 0) {
    errors.push('Low stock threshold cannot be negative');
  }
  
  return errors;
};

// Update a specific review in a product
export const updateProductReview = async (
  productId: string, 
  reviewId: string, 
  updates: Partial<Review>
): Promise<void> => {
  try {
    const productRef = doc(db, 'products', productId);
    const productDoc = await getDoc(productRef);
    
    if (!productDoc.exists()) {
      throw new Error('Product not found');
    }
    
    const productData = productDoc.data() as Product;
    const reviews = productData.reviews || [];
    
    const reviewIndex = reviews.findIndex(r => r.id === reviewId);
    if (reviewIndex === -1) {
      throw new Error('Review not found');
    }
    
    // Update the review
    reviews[reviewIndex] = {
      ...reviews[reviewIndex],
      ...updates,
      updatedAt: new Date().toISOString(),
    };
    
    // Update the product document
    await updateDoc(productRef, { 
      reviews,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating product review:', error);
    throw error;
  }
};

// Delete a specific review from a product
export const deleteProductReview = async (
  productId: string, 
  reviewId: string
): Promise<void> => {
  try {
    const productRef = doc(db, 'products', productId);
    const productDoc = await getDoc(productRef);
    
    if (!productDoc.exists()) {
      throw new Error('Product not found');
    }
    
    const productData = productDoc.data() as Product;
    const reviews = productData.reviews || [];
    
    // Find the review to delete and clean up its images
    const reviewToDelete = reviews.find(r => r.id === reviewId);
    if (reviewToDelete && reviewToDelete.images && reviewToDelete.images.length > 0) {
      try {
        await deleteImagesByUrls(reviewToDelete.images);
        console.log('Successfully cleaned up images from deleted review');
      } catch (cleanupError) {
        console.warn('Failed to cleanup images from deleted review:', cleanupError);
        // Don't block the review deletion if image cleanup fails
      }
    }
    
    // Filter out the review to delete
    const updatedReviews = reviews.filter(r => r.id !== reviewId);
    
    // Update the product document
    await updateDoc(productRef, { 
      reviews: updatedReviews,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error deleting product review:', error);
    throw error;
  }
};

// Batch update multiple reviews in a product
export const batchUpdateProductReviews = async (
  productId: string, 
  reviewIds: string[], 
  updates: Partial<Review>
): Promise<void> => {
  try {
    const productRef = doc(db, 'products', productId);
    const productDoc = await getDoc(productRef);
    
    if (!productDoc.exists()) {
      throw new Error('Product not found');
    }
    
    const productData = productDoc.data() as Product;
    const reviews = productData.reviews || [];
    
    // Update multiple reviews
    const updatedReviews = reviews.map(review => {
      if (reviewIds.includes(review.id)) {
        return {
          ...review,
          ...updates,
          updatedAt: new Date().toISOString(),
        };
      }
      return review;
    });
    
    // Update the product document
    await updateDoc(productRef, { 
      reviews: updatedReviews,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error batch updating product reviews:', error);
    throw error;
  }
};

// Batch delete multiple reviews from a product
export const batchDeleteProductReviews = async (
  productId: string, 
  reviewIds: string[]
): Promise<void> => {
  try {
    const productRef = doc(db, 'products', productId);
    const productDoc = await getDoc(productRef);
    
    if (!productDoc.exists()) {
      throw new Error('Product not found');
    }
    
    const productData = productDoc.data() as Product;
    const reviews = productData.reviews || [];
    
    // Find reviews to delete and collect all their images for cleanup
    const reviewsToDelete = reviews.filter(r => reviewIds.includes(r.id));
    const allImagesToDelete: string[] = [];
    
    reviewsToDelete.forEach(review => {
      if (review.images && review.images.length > 0) {
        allImagesToDelete.push(...review.images);
      }
    });
    
    // Clean up images from all reviews being deleted
    if (allImagesToDelete.length > 0) {
      try {
        await deleteImagesByUrls(allImagesToDelete);
        console.log(`Successfully cleaned up ${allImagesToDelete.length} images from ${reviewsToDelete.length} deleted reviews`);
      } catch (cleanupError) {
        console.warn('Failed to cleanup images from batch deleted reviews:', cleanupError);
        // Don't block the review deletion if image cleanup fails
      }
    }
    
    // Filter out the reviews to delete
    const updatedReviews = reviews.filter(r => !reviewIds.includes(r.id));
    
    // Update the product document
    await updateDoc(productRef, { 
      reviews: updatedReviews,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error batch deleting product reviews:', error);
    throw error;
  }
};

/**
 * Get a monthly snapshot by year and month
 */
export const getMonthlySnapshot = async (year: number, month: number): Promise<MonthlySnapshot | null> => {
  try {
    const snapshotId = `${year}-${month.toString().padStart(2, '0')}`;
    console.log('Getting monthly snapshot:', snapshotId);
    
    const snapshotDoc = doc(db, 'monthly_snapshots', snapshotId);
    const snapshot = await getDoc(snapshotDoc);
    
    if (!snapshot.exists()) {
      console.log('Monthly snapshot not found:', snapshotId);
      return null;
    }
    
    const data = snapshot.data();
    return {
      snapshotId: data.snapshotId,
      year: data.year,
      month: data.month,
      totalProducts: data.totalProducts,
      lowStockProducts: data.lowStockProducts,
      outOfStockProducts: data.outOfStockProducts,
      totalInventoryValue: data.totalInventoryValue,
      totalOrders: data.totalOrders || 0,
      totalRevenue: data.totalRevenue || 0,
      totalCustomers: data.totalCustomers || 0,
      createdAt: data.createdAt.toDate ? data.createdAt.toDate() : data.createdAt,
    };
  } catch (error) {
    console.error('Error getting monthly snapshot:', error);
    return null;
  }
};

/**
 * Get the previous month's snapshot for comparison
 */
export const getPreviousMonthSnapshot = async (): Promise<MonthlySnapshot | null> => {
  try {
    const now = new Date();
    let year = now.getFullYear();
    let month = now.getMonth(); // Current month (0-indexed)
    
    // Go to previous month
    if (month === 0) {
      month = 12;
      year -= 1;
    }
    
    return await getMonthlySnapshot(year, month);
  } catch (error) {
    console.error('Error getting previous month snapshot:', error);
    return null;
  }
};

/**
 * Calculate percentage change between current and previous values
 */
export const calculatePercentageChange = (current: number, previous: number): number => {
  if (previous === 0) {
    return current > 0 ? 100 : 0; // If previous was 0 and current > 0, show 100% increase
  }
  return ((current - previous) / previous) * 100;
};

/**
 * Get statistics with percentage changes from previous month
 */
export const getStatisticsWithComparison = async (): Promise<{
  current: {
    totalProducts: number;
    lowStockProducts: number;
    outOfStockProducts: number;
    totalInventoryValue: number;
  };
  previous: MonthlySnapshot | null;
  percentageChanges: {
    totalProducts: number;
    lowStockProducts: number;
    outOfStockProducts: number;
    totalInventoryValue: number;
  };
}> => {
  try {
    console.log('Getting statistics with comparison...');
    
    // Get current statistics
    const [
      totalProducts,
      lowStockProducts,
      outOfStockProducts,
      totalInventoryValue
    ] = await Promise.all([
      getTotalProductCount(),
      getLowStockProductCount(),
      getOutOfStockProductCount(),
      getTotalInventoryValue()
    ]);
    
    const current = {
      totalProducts,
      lowStockProducts,
      outOfStockProducts,
      totalInventoryValue
    };
    
    // Get previous month's snapshot
    const previous = await getPreviousMonthSnapshot();
    
    // Calculate percentage changes
    const percentageChanges = {
      totalProducts: previous ? calculatePercentageChange(current.totalProducts, previous.totalProducts) : 0,
      lowStockProducts: previous ? calculatePercentageChange(current.lowStockProducts, previous.lowStockProducts) : 0,
      outOfStockProducts: previous ? calculatePercentageChange(current.outOfStockProducts, previous.outOfStockProducts) : 0,
      totalInventoryValue: previous ? calculatePercentageChange(current.totalInventoryValue, previous.totalInventoryValue) : 0
    };
    
    console.log('Statistics comparison:', { current, previous, percentageChanges });
    
    return {
      current,
      previous,
      percentageChanges
    };
  } catch (error) {
    console.error('Error getting statistics with comparison:', error);
    throw error;
  }
};

/**
 * Get top selling products this month using pre-computed salesThisMonth field
 */
export const getTopSellingProductsThisMonth = async (limitCount: number = 5): Promise<Product[]> => {
  try {
    console.log(`Getting top ${limitCount} selling products this month...`);
    
    const productsCollection = collection(db, 'products');
    
    // Query for top products by salesThisMonth (pre-computed field)
    const topProductsQuery = query(
      productsCollection,
      where('disabled', '!=', true),
      where('salesThisMonth', '>', 0), // Only products with sales this month
      orderBy('salesThisMonth', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(topProductsQuery);
    
    if (snapshot.empty) {
      console.log('No products with sales found for this month');
      return [];
    }
    
    const topProducts = snapshot.docs.map(processProductData);
    
    console.log(`Found ${topProducts.length} top selling products:`, 
      topProducts.map(p => ({ 
        name: p.name, 
        salesThisMonth: p.salesThisMonth, 
        revenueThisMonth: p.revenueThisMonth 
      }))
    );
    
    return topProducts;
  } catch (error) {
    console.error('Error getting top selling products this month:', error);
    throw new Error('Failed to get top selling products this month');
  }
};

/**
 * Fetch all enabled products (for CSV export and other operations)
 */
export const fetchEnabledProducts = async (): Promise<Product[]> => {
  try {
    console.log('Fetching all enabled products...');
    
    const productsCollection = collection(db, 'products');
    const snapshot = await getDocs(
      query(
        productsCollection,
        where('disabled', '!=', true),
        orderBy('name')
      )
    );
    
    const products = snapshot.docs.map(processProductData);
    console.log(`Found ${products.length} enabled products`);
    
    return products;
  } catch (error) {
    console.error('Error fetching enabled products:', error);
    throw new Error('Failed to fetch enabled products');
  }
};

/**
 * Convert products array to CSV format
 */
export const convertProductsToCSV = (products: Product[]): string => {
  if (products.length === 0) {
    return '';
  }

  // Define CSV headers
  const headers = [
    'ID',
    'Name',
    'Description',
    'Price',
    'On Sale Price',
    'Category',
    'Brand',
    'Tags',
    'Is New',
    'Is Best Seller',
    'On Sale',
    'Stock Quantity',
    'Low Stock Threshold',
    'Net Weight',
    'Weight Unit',
    'Listing Quantity',
    'Inventory Value',
    'Available Shipping Options',
    'SEO Title',
    'SEO Description',
    'Sales Count',
    'Sales This Month',
    'Revenue This Month',
    'Manufacturer',
    'HS Code',
    'Origin Country',
    'Images',
    'Reviews Count',
    'Average Rating',
    'Created At',
    'Updated At'
  ];

  // Helper function to escape CSV values
  const escapeCSV = (value: string | number | boolean | null | undefined): string => {
    if (value === null || value === undefined) {
      return '';
    }
    
    const str = String(value);
    // If the value contains comma, quote, or newline, wrap in quotes and escape quotes
    if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  // Helper function to calculate average rating
  const calculateAverageRating = (reviews: Review[] = []): string => {
    const approvedReviews = reviews.filter(r => r.status === 'verified');
    if (approvedReviews.length === 0) return '0';
    
    const totalRating = approvedReviews.reduce((sum, review) => sum + review.rating, 0);
    return (totalRating / approvedReviews.length).toFixed(2);
  };

  // Convert products to CSV rows
  const rows = products.map(product => [
    escapeCSV(product.id),
    escapeCSV(product.name),
    escapeCSV(product.description),
    escapeCSV(product.price?.toFixed(2) || '0.00'),
    escapeCSV(product.onSalePrice?.toFixed(2) || ''),
    escapeCSV(product.category || ''),
    escapeCSV(product.brand || ''),
    escapeCSV(product.tags?.join('; ') || ''),
    escapeCSV(product.isNew ? 'Yes' : 'No'),
    escapeCSV(product.isBestSeller ? 'Yes' : 'No'),
    escapeCSV(product.onSale ? 'Yes' : 'No'),
    escapeCSV(product.stockQuantity || 0),
    escapeCSV(product.lowStockThreshold || 10),
    escapeCSV(product.netWeight || ''),
    escapeCSV(product.weightUnit || ''),
    escapeCSV(product.listingQuantity || 1),
    escapeCSV(product.inventoryValue?.toFixed(2) || '0.00'),
    escapeCSV(product.availableShippingOptions?.join('; ') || ''),
    escapeCSV(product.seoTitle || ''),
    escapeCSV(product.seoDescription || ''),
    escapeCSV(product.salesCount || 0),
    escapeCSV(product.salesThisMonth || 0),
    escapeCSV(product.revenueThisMonth?.toFixed(2) || '0.00'),
    escapeCSV(product.manufacturer || ''),
    escapeCSV(product.hsCode || ''),
    escapeCSV(product.originCountry || ''),
    escapeCSV(product.images?.join('; ') || ''),
    escapeCSV(product.reviews?.filter(r => r.status === 'verified').length || 0),
    escapeCSV(calculateAverageRating(product.reviews)),
    escapeCSV(product.createdAt ? new Date(product.createdAt).toISOString() : ''),
    escapeCSV(product.updatedAt ? new Date(product.updatedAt).toISOString() : '')
  ]);

  // Combine headers and rows
  const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  
  return csvContent;
};

/**
 * Export enabled products to CSV and trigger download
 */
export const exportProductsToCSV = async (): Promise<void> => {
  try {
    console.log('Starting CSV export of enabled products...');
    
    // Fetch all enabled products
    const products = await fetchEnabledProducts();
    
    if (products.length === 0) {
      throw new Error('No enabled products found to export');
    }
    
    // Convert to CSV
    const csvContent = convertProductsToCSV(products);
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      // Generate filename with current date
      const currentDate = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `enabled_products_${currentDate}.csv`);
      
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL
      URL.revokeObjectURL(url);
      
      console.log(`Successfully exported ${products.length} enabled products to CSV`);
    } else {
      throw new Error('CSV download not supported in this browser');
    }
  } catch (error) {
    console.error('Error exporting products to CSV:', error);
    throw error;
  }
};

export interface MissingInternationalShippingProduct {
  id: string;
  brand: string;
  name: string;
  weight: string;
}

const isBlankString = (value: unknown): boolean => {
  if (typeof value !== 'string') return true;
  return value.trim().length === 0;
};

const formatProductWeight = (product: Product): string => {
  if (product.netWeight === null || product.netWeight === undefined) return '';
  const unit = product.weightUnit || 'g';
  return `${product.netWeight}${unit}`;
};

const formatProductSize = (product: Product): string => {
  if (product.netWeight === null || product.netWeight === undefined) return '';
  const unit = product.weightUnit || 'g';
  return `${product.netWeight} ${unit}`;
};

const isMissingInternationalShippingInfo = (product: Product): boolean => {
  return (
    isBlankString(product.manufacturer) ||
    isBlankString(product.hsCode) ||
    isBlankString(product.originCountry)
  );
};

const toHs6 = (hsCode: unknown): string => {
  if (typeof hsCode !== 'string') return '';
  const digitsOnly = hsCode.replace(/[^\d]/g, '');
  return digitsOnly.slice(0, 6);
};

/**
 * Fetch up to `max` products missing international shipping/customs fields.
 * (Missing any of: manufacturer, hsCode, originCountry)
 */
export const fetchProductsMissingInternationalShippingInfo = async (
  max: number = 50
): Promise<MissingInternationalShippingProduct[]> => {
  try {
    const maxItems = Math.max(0, Math.min(50, max));
    if (maxItems === 0) return [];

    const productsCollection = collection(db, 'products');

    const results: MissingInternationalShippingProduct[] = [];
    const pageSize = 200;
    let lastDoc: QueryDocumentSnapshot<DocumentData> | null = null;

    while (results.length < maxItems) {
      const pageQuery = lastDoc
        ? query(productsCollection, orderBy('__name__'), startAfter(lastDoc), limit(pageSize))
        : query(productsCollection, orderBy('__name__'), limit(pageSize));

      const snapshot = await getDocs(pageQuery);
      if (snapshot.empty) break;

      for (const docSnap of snapshot.docs) {
        const product = processProductData(docSnap);
        // Only include enabled products (disabled !== true)
        if (product.disabled === true) continue;
        if (!isMissingInternationalShippingInfo(product)) continue;

        results.push({
          id: product.id,
          brand: product.brand || '',
          name: product.name || '',
          weight: formatProductWeight(product),
        });

        if (results.length >= maxItems) break;
      }

      lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
      if (snapshot.docs.length < pageSize) break;
    }

    return results;
  } catch (error) {
    console.error('Error fetching products missing international shipping info:', error);
    throw new Error('Failed to fetch products missing international shipping info');
  }
};

/**
 * Export up to 50 products missing international shipping/customs fields to JSON and trigger download.
 */
export const exportProductsMissingInternationalShippingInfoToJSON = async (): Promise<{
  exported: number;
}> => {
  const items = await fetchProductsMissingInternationalShippingInfo(50);

  const jsonContent = JSON.stringify(items, null, 2);
  const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download === undefined) {
    throw new Error('JSON download not supported in this browser');
  }

  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);

  const currentDate = new Date().toISOString().split('T')[0];
  link.setAttribute('download', `products_missing_international_shipping_${currentDate}.json`);

  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return { exported: items.length };
};

export interface CommodityCodesMissingProduct {
  id: string;
  name: string;
  brand: string;
  description: string;
  size: string;
  hs6: string;
}

const isMissingCommodityCodes = (value: unknown): boolean => {
  if (!value || typeof value !== 'object') return true;
  return Object.keys(value as Record<string, unknown>).length === 0;
};

/**
 * Fetch up to `max` enabled products missing `commodityCodes`.
 */
export const fetchEnabledProductsMissingCommodityCodes = async (
  max: number = 50
): Promise<CommodityCodesMissingProduct[]> => {
  try {
    const maxItems = Math.max(0, Math.min(50, max));
    if (maxItems === 0) return [];

    const productsCollection = collection(db, 'products');
    const results: CommodityCodesMissingProduct[] = [];
    const pageSize = 200;
    let lastDoc: QueryDocumentSnapshot<DocumentData> | null = null;

    while (results.length < maxItems) {
      const pageQuery = lastDoc
        ? query(productsCollection, orderBy('__name__'), startAfter(lastDoc), limit(pageSize))
        : query(productsCollection, orderBy('__name__'), limit(pageSize));

      const snapshot = await getDocs(pageQuery);
      if (snapshot.empty) break;

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const product = processProductData(docSnap);

        // Only include enabled products (disabled !== true)
        if (product.disabled === true) continue;
        if (!isMissingCommodityCodes((data as any).commodityCodes)) continue;

        results.push({
          id: product.id,
          name: product.name || '',
          brand: product.brand || '',
          description: product.description || '',
          size: formatProductSize(product),
          hs6: toHs6(product.hsCode),
        });

        if (results.length >= maxItems) break;
      }

      lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
      if (snapshot.docs.length < pageSize) break;
    }

    return results;
  } catch (error) {
    console.error('Error fetching enabled products missing commodityCodes:', error);
    throw new Error('Failed to fetch enabled products missing commodityCodes');
  }
};

/**
 * Export up to 50 enabled products missing `commodityCodes` to JSON and trigger download.
 */
export const exportEnabledProductsMissingCommodityCodesToJSON = async (): Promise<{
  exported: number;
}> => {
  const products = await fetchEnabledProductsMissingCommodityCodes(50);

  const jsonPayload = { products };
  const jsonContent = JSON.stringify(jsonPayload, null, 2);
  const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download === undefined) {
    throw new Error('JSON download not supported in this browser');
  }

  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);

  const currentDate = new Date().toISOString().split('T')[0];
  link.setAttribute('download', `enabled_products_missing_commodity_codes_${currentDate}.json`);

  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return { exported: products.length };
};

/**
 * Set the specified number of most recent enabled products to "new" status
 */
export const setRecentProductsToNew = async (
  count: number
): Promise<{ updated: number; markedNew: number; clearedNew: number; errors: number }> => {
  try {
    console.log(`Setting exactly ${count} most recent enabled products to new...`);
    
    if (count <= 0) {
      throw new Error('Count must be greater than 0');
    }
    
    const productsCollection = collection(db, 'products');
    
    const recentProductsQuery = query(
      productsCollection,
      orderBy('createdAt', 'desc')
    );

    const [allProductsSnapshot, currentNewSnapshot] = await Promise.all([
      getDocs(recentProductsQuery),
      getDocs(query(productsCollection, where('isNew', '==', true)))
    ]);

    if (allProductsSnapshot.empty) {
      console.log('No enabled products found');
      return { updated: 0, markedNew: 0, clearedNew: 0, errors: 0 };
    }

    const selectedProducts = allProductsSnapshot.docs
      .filter(doc => {
        const data = doc.data();
        return data.disabled !== true;
      })
      .slice(0, count);

    const selectedProductIds = new Set(selectedProducts.map(doc => doc.id));
    const updates: Array<{
      ref: QueryDocumentSnapshot<DocumentData>['ref'];
      isNew: boolean;
    }> = [];

    selectedProducts.forEach(doc => {
      const data = doc.data();
      if (data.isNew !== true) {
        updates.push({ ref: doc.ref, isNew: true });
      }
    });

    currentNewSnapshot.docs.forEach(doc => {
      if (!selectedProductIds.has(doc.id)) {
        updates.push({ ref: doc.ref, isNew: false });
      }
    });

    if (updates.length === 0) {
      console.log('Product new badge state already matches requested count');
      return { updated: 0, markedNew: 0, clearedNew: 0, errors: 0 };
    }

    let updateCount = 0;
    let markedNew = 0;
    let clearedNew = 0;

    for (let i = 0; i < updates.length; i += 450) {
      const batch = writeBatch(db);
      const chunk = updates.slice(i, i + 450);

      chunk.forEach(update => {
        batch.update(update.ref, {
          isNew: update.isNew,
          updatedAt: serverTimestamp()
        });
        updateCount++;
        if (update.isNew) {
          markedNew++;
        } else {
          clearedNew++;
        }
      });

      await batch.commit();
    }

    console.log(
      `Successfully reconciled new badge state. Updated: ${updateCount}, marked new: ${markedNew}, cleared: ${clearedNew}`
    );
    return { updated: updateCount, markedNew, clearedNew, errors: 0 };
  } catch (error) {
    console.error('Error setting recent products to new:', error);
    throw new Error('Failed to set recent products to new');
  }
}; 
